import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Database, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import LockedKpiStrip from "@/components/dashboards/LockedKpiStrip";
import ApplyThemePill from "@/components/dashboards/ApplyThemePill";
import { useAuth } from "@/lib/auth-context";
import { useEntitlements } from "@/lib/use-entitlements";
import { useScopeFilter } from "@/lib/scope-filter-context";
import { useBreadcrumbLabel } from "@/lib/breadcrumb-labels";

/**
 * Gallery template preview — renders a prebuilt template (kind=prebuilt_dashboard)
 * with the embedded `sample_data` blob so users can SEE what it looks like
 * before instantiating it against their tenant.
 *
 * The page reuses LockedKpiStrip with `previewMode=true` — the strip then
 * routes its joined-query calls to /gallery/{id}/preview-query and the
 * distinct-values calls to /gallery/{id}/distinct-preview.
 *
 * Yellow banner explains the sample is synthetic; "Use this template" CTA
 * navigates to /dashboards/instantiate/{id} which calls the existing
 * instantiation endpoint and lands the user on a real dashboard.
 */
export default function GalleryPreview() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const { entitled, limitReached, openUpgrade } = useEntitlements();
    // Plan gate — instantiating a template creates a real dashboard, so it
    // needs the `prebuilt_dashboards` feature AND an open dashboard slot.
    const useLocked = !isSuperAdmin && (!entitled("prebuilt_dashboards") || limitReached("max_dashboards"));
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewSource, setPreviewSource] = useState(null);
    const [instantiating, setInstantiating] = useState(false);
    const { tenantId: scopeTenantId, orgId: scopeOrgId } = useScopeFilter();
    // iter — Register the template's friendly name for the breadcrumb.
    useBreadcrumbLabel(id, template?.name);
    // iter-121.61 — Detect whether we landed via /dashboards/preview/:id
    // (the canonical entry point from the Dashboards → Prebuilt tab) or
    // the legacy /gallery/preview/:id alias (kept for back-compat). The
    // eyebrow label + back-button destination follow the entry point so
    // users don't see "Gallery" referenced when they came from the
    // Dashboards page.
    const cameFromDashboards = location.pathname.startsWith("/dashboards/");
    const backLabel = cameFromDashboards ? "Back to Dashboards" : "Back to Gallery";
    const backHref = cameFromDashboards ? "/dashboards" : "/gallery";

    const load = React.useCallback(() => {
        if (!id) return;
        api.get(`/dashboards/templates`)
           .then((r) => {
               const list = r.data || [];
               const found = list.find((t) => t.id === id);
               setTemplate(found || null);
           })
           .catch(() => setTemplate(null));
    }, [id]);

    useEffect(() => {
        setLoading(true);
        load();
        api.get(`/gallery/${id}/preview-source`).then((r) => setPreviewSource(r.data)).catch(() => setPreviewSource(null));
        // Since load is defined inside, we can call finally in a local promise chain
        api.get(`/dashboards/templates`)
           .finally(() => setLoading(false));
    }, [load]);

    useEffect(() => {
        const handleRefresh = (e) => {
            if (e.detail?.dashboardId === id) {
                load();
            }
        };
        window.addEventListener("dashboard:refresh", handleRefresh);
        return () => window.removeEventListener("dashboard:refresh", handleRefresh);
    }, [id, load]);

    const useThisTemplate = async () => {
        if (!template) return;
        setInstantiating(true);
        try {
            const params = {};
            if (scopeTenantId) params.tenant_id = scopeTenantId;
            if (scopeOrgId) params.org_id = scopeOrgId;
            const r = await api.post(`/dashboards/from-template/${template.id}`, null, { params });
            const dashId = r.data?.id || r.data?.dashboard_id;
            toast.success(`${template.name} added to your dashboards`);
            if (dashId) navigate(`/dashboards/${dashId}`);
            else navigate("/dashboards");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not instantiate template");
        } finally {
            setInstantiating(false);
        }
    };

    if (loading) {
        return (
            <div className="p-12 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading preview…
            </div>
        );
    }
    if (!template) {
        return (
            <div className="p-12 text-center" data-testid="gallery-preview-not-found">
                <div className="text-base font-medium mb-2">Template not found</div>
                <Link to={backHref} className="text-primary text-sm hover:underline">← {backLabel}</Link>
            </div>
        );
    }

    // Build a dashboard-shaped object from the template so LockedKpiStrip
    // renders without modification. `id` is set to the template id and
    // `previewMode` toggles the alternate query routing inside the strip.
    const previewDashboard = {
        id: template.id,
        name: template.name,
        source_template_version: template.version,
        source_gallery_template_id: template.id,
        data_model_bindings: (template.bind_to_models || []).map((m) => ({ model_id: m })),
        widgets: template.widgets || [],
        locked_kpis: template.locked_kpis && template.locked_kpis.length > 0
            ? template.locked_kpis.map((item) => {
                if (typeof item === "string") {
                    return (template.widgets || []).find((w) => w.id === item) || item;
                }
                const found = (template.widgets || []).find((w) => w.id === (item?.id || item));
                return found ? { ...found, ...item } : item;
            }).filter(Boolean)
            : (template.widgets || []).filter((w) => w.chart_type === "kpi" && (w.locked || false)),
        kpi_pool: template.kpi_pool || [],
        filters: template.filters || [],
        where: template.where || [],
        previewMode: true,
        is_canonical: template.is_canonical,
        created_by: template.created_by,
    };

    return (
        <div className="fadein">
            <div className="px-6 pt-6">
                <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-1">
                            {cameFromDashboards ? "Prebuilt Template" : "Template Preview"}
                        </div>
                        <h1 className="text-2xl font-medium">{template.name}</h1>
                        {template.description && (
                            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{template.description}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        {isSuperAdmin && (
                            <ApplyThemePill testId="gallery-preview-apply-theme-btn" />
                        )}
                        {useLocked ? (
                        <Button onClick={() => openUpgrade()}
                                className="rounded-sm" variant="outline" data-testid="gallery-preview-use-btn"
                                title="Using this template isn't available on your current plan — upgrade to unlock">
                            <Lock className="h-4 w-4 mr-2" />
                            Use this template
                            <span className="ml-2 text-[9px] font-mono uppercase rounded bg-primary/10 text-primary px-1.5 py-0.5">Upgrade</span>
                        </Button>
                        ) : (
                        <Button onClick={useThisTemplate} disabled={instantiating}
                                className="rounded-sm" data-testid="gallery-preview-use-btn">
                            {instantiating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                            Use this template
                        </Button>
                        )}
                    </div>
                </div>

                {/* Preview banner — says whether the preview shows the model's stored rows or fallback sample rows. */}
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 mb-4 flex items-start gap-3"
                     data-testid="gallery-preview-banner">
                    <Database className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    {previewSource?.data_source === "live" ? (
                        <div className="text-xs flex-1" data-testid="gallery-preview-banner-live">
                            <span className="font-medium">Showing data from the model.</span>{" "}
                            Every chart and filter uses the {Number(previewSource.rows).toLocaleString()} rows stored in this template's data model. Click <span className="font-medium">Use this template</span> to add it to your dashboards.
                        </div>
                    ) : (
                        <div className="text-xs flex-1" data-testid="gallery-preview-banner-sample">
                            <span className="font-medium">Showing sample data.</span>{" "}
                            This template's data model has no rows yet, so the numbers are synthetic. Click <span className="font-medium">Use this template</span> to instantiate it against your entity's real data.
                        </div>
                    )}
                </div>
            </div>

            <div className="px-6 pb-6">
                <LockedKpiStrip
                    dashboard={previewDashboard}
                    onDashboardUpdated={(updated) => {
                        setTemplate(updated);
                    }}
                />
            </div>
        </div>
    );
}
